import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Shield, User, Users as UsersIcon, AlertCircle, TrendingUp, CheckCircle2, XCircle, Crown, Zap, Upload, X, Camera } from 'lucide-react';
import api from '../services/api';

const roleLabels = { 
  admin: 'Administrador', 
  manager: 'Gerente', 
  attendant: 'Atendente',
  tenant_admin: 'Admin da Empresa'
};

const roleColors = { 
  admin: 'bg-purple-100 text-purple-700', 
  manager: 'bg-blue-100 text-blue-700', 
  attendant: 'bg-green-100 text-green-700',
  tenant_admin: 'bg-orange-100 text-orange-700'
};

const roleIcons = {
  admin: Crown,
  manager: Shield,
  attendant: User,
  tenant_admin: Crown
};

// ⭐ NOVO: formata a duração da sessão atual, tipo "2h 15min" ou "45min"
const formatOnlineDuration = (onlineSince) => {
  if (!onlineSince) return null;
  const diffMs = Date.now() - new Date(onlineSince).getTime();
  const totalMin = Math.max(0, Math.floor(diffMs / 60000));
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  if (hours > 0) return `${hours}h ${minutes}min`;
  return `${minutes}min`;
};

function Users() {
  const [users, setUsers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
const [avatarFile, setAvatarFile] = useState(null);
  const [tenantLimits, setTenantLimits] = useState({
    current: 0,
    max: 5,
    plan: 'basic',
    percentage: 0
  });
  const [form, setForm] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    role: 'attendant', 
    sector_id: '' 
  });

    useEffect(() => {
    loadData();
    loadTenantLimits();

    // ⭐ NOVO: atualiza só o status online/offline a cada 15s, sem piscar a tela
    const interval = setInterval(() => {
      api.get('/api/users').then(res => setUsers(res.data)).catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, []);
  const loadData = async () => {
    try {
      const [usersRes, sectorsRes] = await Promise.all([
        api.get('/api/users'),
        api.get('/api/sectors')
      ]);
      setUsers(usersRes.data);
      setSectors(sectorsRes.data);
    } catch (err) { 
      console.error('Erro ao carregar dados:', err);
    }
  };

  // ⭐ CARREGAR LIMITES DO TENANT
  const loadTenantLimits = async () => {
    try {
      const res = await api.get('/api/tenants/limits');
      const { current_users, max_users, plan_type } = res.data;
      const percentage = max_users > 0 ? (current_users / max_users) * 100 : 0;
      
      setTenantLimits({
        current: current_users,
        max: max_users,
        plan: plan_type,
        percentage: percentage
      });
    } catch (err) {
      console.error('Erro ao carregar limites:', err);
      // Fallback: usar valores padrão
      setTenantLimits({
        current: users.length,
        max: 5,
        plan: 'basic',
        percentage: (users.length / 5) * 100
      });
    }
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  try {
    // ⭐ Converter sector_id vazio para null
    const formData = {
      ...form,
      sector_id: form.sector_id === '' ? null : parseInt(form.sector_id)
    };

    if (editingUser) {
      // NOVO: nao manda password vazio na edicao - senao a validacao do
      // backend rejeitava QUALQUER edicao onde a senha nao fosse alterada
      if (!formData.password) {
        delete formData.password;
      }
      await api.put(`/api/users/${editingUser.id}`, formData);
      
      // ⭐ Upload de avatar se houver
      if (avatarFile) {
        const avatarFormData = new FormData();
        avatarFormData.append('avatar', avatarFile);
        await api.post(`/api/users/${editingUser.id}/avatar`, avatarFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      alert('✅ Usuário atualizado com sucesso!');
    } else {
      const res = await api.post('/api/users', formData);
      
      // ⭐ Upload de avatar após criar usuário
      if (avatarFile && res.data.id) {
        const avatarFormData = new FormData();
        avatarFormData.append('avatar', avatarFile);
        await api.post(`/api/users/${res.data.id}/avatar`, avatarFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      alert('✅ Usuário criado com sucesso!');
    }
    
    setShowModal(false);
    setEditingUser(null);
    setForm({ name: '', email: '', password: '', role: 'attendant', sector_id: '' });
    setAvatarFile(null);
    setAvatarPreview(null);
    loadData();
    loadTenantLimits();
  } catch (err) {
    const errorData = err.response?.data;
    
    if (errorData?.details?.message) {
      alert(
        `⚠️ ${errorData.error}\n\n` +
        `${errorData.details.message}\n\n` +
        `━━━━━━━━━━━━━━━━━━━━\n` +
        `📊 Uso atual: ${errorData.details.current}/${errorData.details.max}\n` +
        `📦 Plano: ${errorData.details.plan}\n` +
        `━━━━━━━━━━━━━━━━━━━━\n\n` +
        `💡 Para aumentar o limite, entre em contato com o suporte ou faça upgrade do plano.`
      );
    } else {
      alert('Erro: ' + (errorData?.error || err.message));
    }
  }
};


const handleAvatarChange = (e) => {
  const file = e.target.files[0];
  if (file) {
    if (file.size > 5 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 5MB');
      return;
    }
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
    };
    reader.readAsDataURL(file);
  }
};

const handleRemoveAvatar = () => {
  setAvatarFile(null);
  setAvatarPreview(null);
};

const handleEdit = (user) => {
  setEditingUser(user);
  setForm({ 
    name: user.name, 
    email: user.email, 
    password: '', 
    role: user.role, 
    sector_id: user.sector_id || '',
    notification_sound_enabled: user.notification_sound_enabled !== false,
    can_access_envios: user.can_access_envios !== false
  });
  setAvatarPreview(user.avatar_url ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${user.avatar_url}` : null);
  setAvatarFile(null);
  setShowModal(true);
};

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try { 
        await api.delete(`/api/users/${id}`);
        alert('✅ Usuário excluído com sucesso!');
        loadData();
        loadTenantLimits();
      } catch (err) { 
        alert('Erro ao excluir: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await api.put(`/api/users/${user.id}`, {
        is_active: !user.is_active
      });
      loadData();
      loadTenantLimits();
    } catch (err) {
      alert('Erro ao alterar status: ' + (err.response?.data?.error || err.message));
    }
  };

  // ⭐ FILTRAGEM
  const filtered = users.filter(u => {
    const matchesSearch = 
      u.name.toLowerCase().includes(search.toLowerCase()) || 
      u.email.toLowerCase().includes(search.toLowerCase());
    
    const matchesRole = !filterRole || u.role === filterRole;
    const matchesStatus = !filterStatus || 
      (filterStatus === 'active' && u.is_active) ||
      (filterStatus === 'inactive' && !u.is_active);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  // ⭐ ESTATÍSTICAS
  const stats = {
    total: users.length,
    active: users.filter(u => u.is_active).length,
    inactive: users.filter(u => !u.is_active).length,
    admins: users.filter(u => u.role === 'admin' || u.role === 'tenant_admin').length,
    managers: users.filter(u => u.role === 'manager').length,
    attendants: users.filter(u => u.role === 'attendant').length
  };

  // ⭐ COR DA BARRA DE PROGRESSO
  const getProgressColor = () => {
    if (tenantLimits.percentage >= 100) return 'bg-red-500';
    if (tenantLimits.percentage >= 80) return 'bg-orange-500';
    if (tenantLimits.percentage >= 60) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getProgressBgColor = () => {
    if (tenantLimits.percentage >= 100) return 'bg-red-50';
    if (tenantLimits.percentage >= 80) return 'bg-orange-50';
    if (tenantLimits.percentage >= 60) return 'bg-yellow-50';
    return 'bg-green-50';
  };

  const isLimitReached = tenantLimits.current >= tenantLimits.max;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestão de Usuários</h1>
          <p className="text-gray-500">Administre perfis, permissões e acessos da equipe.</p>
        </div>
        <button 
          onClick={() => { 
            setEditingUser(null); 
            setForm({ name: '', email: '', password: '', role: 'attendant', sector_id: '' }); 
            setShowModal(true); 
          }}
          disabled={isLimitReached}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium shadow-lg transition-all ${
            isLimitReached
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
              : 'bg-green-600 text-white hover:bg-green-700 shadow-green-100'
          }`}
        >
          <Plus size={18} /> 
          {isLimitReached ? 'Limite Atingido' : 'Novo Usuário'}
        </button>
      </div>

      {/* ⭐ CARDS DE ESTATÍSTICAS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Total */}
        <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <UsersIcon size={24} className="text-gray-400" />
          </div>
        </div>

        {/* Ativos */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-2xl border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-green-700">Ativos</p>
              <p className="text-2xl font-bold text-green-900">{stats.active}</p>
            </div>
            <CheckCircle2 size={24} className="text-green-600" />
          </div>
        </div>

        {/* Inativos */}
        <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-2xl border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-red-700">Inativos</p>
              <p className="text-2xl font-bold text-red-900">{stats.inactive}</p>
            </div>
            <XCircle size={24} className="text-red-600" />
          </div>
        </div>

        {/* ⭐ USO DO PLANO */}
        <div className={`p-4 rounded-2xl border ${getProgressBgColor()} border-opacity-50`}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs text-gray-600 font-medium">Uso do Plano</p>
              <p className="text-2xl font-bold text-gray-900">
                {tenantLimits.current}/{tenantLimits.max}
              </p>
            </div>
            <TrendingUp size={24} className={getProgressColor().replace('bg-', 'text-')} />
          </div>
          {/* Barra de Progresso */}
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className={`h-full ${getProgressColor()} transition-all duration-500`}
              style={{ width: `${Math.min(tenantLimits.percentage, 100)}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Plano: <span className="font-semibold capitalize">{tenantLimits.plan}</span>
          </p>
        </div>
      </div>

      {/* ⭐ ALERTA DE LIMITE */}
      {isLimitReached && (
        <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle size={24} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-900">
              ⚠️ Limite de Usuários Atingido!
            </p>
            <p className="text-xs text-red-700 mt-1">
              Seu plano <strong>{tenantLimits.plan}</strong> permite no máximo <strong>{tenantLimits.max} usuários</strong>.
              Para adicionar mais usuários, entre em contato com o suporte ou faça upgrade do plano.
            </p>
          </div>
        </div>
      )}

      {/* ⭐ FILTROS */}
      <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Busca */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input 
              type="text" 
              placeholder="Buscar por nome ou email..." 
              value={search} 
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
            />
          </div>

          {/* Filtro por Perfil */}
          <select
            value={filterRole}
            onChange={e => setFilterRole(e.target.value)}
            className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
          >
            <option value="">Todos os perfis</option>
            <option value="tenant_admin">Administrador</option>
            <option value="manager">Gerente</option>
            <option value="attendant">Atendente</option>
          </select>

          {/* Filtro por Status */}
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
          >
            <option value="">Todos os status</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </select>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="px-6 py-4 font-semibold">Usuário</th>
              <th className="px-6 py-4 font-semibold">Email</th>
              <th className="px-6 py-4 font-semibold">Perfil</th>
              <th className="px-6 py-4 font-semibold">Setor</th>
              <th className="px-6 py-4 font-semibold">Status</th>
              <th className="px-6 py-4 font-semibold">Agora</th>
              <th className="px-6 py-4 font-semibold">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map(user => {
              const RoleIcon = roleIcons[user.role] || User;
              return (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                     <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm overflow-hidden flex-shrink-0 bg-gradient-to-br from-green-500 to-green-600 text-white">
  {user.avatar_url ? (
    <img 
      src={`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${user.avatar_url}`} 
      alt={user.name}
      className="w-full h-full object-cover"
      onError={(e) => {
        e.target.onerror = null;
        e.target.style.display = 'none';
        e.target.parentElement.textContent = user.name.charAt(0).toUpperCase();
      }}
    />
  ) : (
    user.name.charAt(0).toUpperCase()
  )}
</div>
                      <span className="font-medium text-gray-900">{user.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600 text-sm">{user.email}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${roleColors[user.role] || 'bg-gray-100 text-gray-700'}`}>
                      <RoleIcon size={12} />
                      {roleLabels[user.role] || user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600 text-sm">{user.sector_name || '—'}</td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggleActive(user)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${
                        user.is_active 
                          ? 'bg-green-50 text-green-700 hover:bg-green-100' 
                          : 'bg-red-50 text-red-700 hover:bg-red-100'
                      }`}
                    >
                                            {user.is_active ? (
                        <>
                          <CheckCircle2 size={12} />
                          Ativo
                        </>
                      ) : (
                        <>
                          <XCircle size={12} />
                          Inativo
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
                      user.is_online ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${user.is_online ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                      {user.is_online ? 'Online' : 'Offline'}
                    </span>
                    {/* ⭐ NOVO: há quanto tempo está conectado nessa sessão */}
                    {user.is_online && user.online_since && (
                      <p className="text-xs text-gray-400 mt-1">
                        Conectado há {formatOnlineDuration(user.online_since)}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleEdit(user)} 
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Editar"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        onClick={() => handleDelete(user.id)} 
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Excluir"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-400">
                  <UsersIcon size={32} className="mx-auto mb-2 opacity-30" />
                  <p>Nenhum usuário encontrado.</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
            <h2 className="text-lg font-bold mb-4">{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</h2>
            
            {/* ⭐ ALERTA DE LIMITE NO MODAL */}
            {!editingUser && isLimitReached && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                <p className="text-xs text-red-700 font-medium">
                  ⚠️ Limite atingido! Não é possível criar novos usuários.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Avatar Upload */}
<div>
  <label className="block text-xs font-medium text-gray-700 mb-2">Foto do Usuário</label>
  <div className="flex items-center gap-4">
    {/* Preview */}
    <div className="relative">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
        {avatarPreview ? (
          <img src={avatarPreview} alt="Preview" className="w-full h-full object-cover" />
        ) : (
          form.name.charAt(0).toUpperCase()
        )}
      </div>
    </div>
    
    {/* Upload Button */}
    <div className="flex-1">
      
     
    </div>
  </div>
</div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nome completo *</label>
                <input 
                  type="text" 
                  placeholder="Ex: João Silva" 
                  value={form.name} 
                  onChange={e => setForm({...form, name: e.target.value})} 
                  required
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
                <input 
                  type="email" 
                  placeholder="exemplo@email.com" 
                  value={form.email} 
                  onChange={e => setForm({...form, email: e.target.value})} 
                  required
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {editingUser ? 'Nova senha (deixe vazio para manter)' : 'Senha *'}
                </label>
                <input 
                  type="password" 
                  placeholder={editingUser ? "••••••••" : "Mínimo 6 caracteres"} 
                  value={form.password} 
                  onChange={e => setForm({...form, password: e.target.value})}
                  required={!editingUser}
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Perfil *</label>
                <select 
                  value={form.role} 
                  onChange={e => setForm({...form, role: e.target.value})}
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
                >
                  <option value="attendant">Atendente</option>
                  <option value="manager">Gerente</option>
                  <option value="tenant_admin">Administrador</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Setor</label>
                <select 
                  value={form.sector_id} 
                  onChange={e => setForm({...form, sector_id: e.target.value})}
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
                >
                  <option value="">Sem setor vinculado</option>
                  {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>

                            {editingUser && (
                <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Som de notificação</p>
                    <p className="text-xs text-gray-500">Toca um som quando chegar mensagem nova pra esse usuário</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm({...form, notification_sound_enabled: !form.notification_sound_enabled})}
                    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                      form.notification_sound_enabled ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        form.notification_sound_enabled ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </div>
              )}
              {/* ⭐ NOVO: habilita/desabilita a tela de Envios (assinatura digital) por usuário */}
              {editingUser && (
                <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Acesso a Envios (assinatura digital)</p>
                    <p className="text-xs text-gray-500">Permite esse usuário enviar documentos para assinatura eletrônica</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm({...form, can_access_envios: !form.can_access_envios})}
                    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                      form.can_access_envios ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        form.can_access_envios ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </div>
              )}
              <div className="flex gap-3 justify-end pt-2">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  disabled={!editingUser && isLimitReached}
                  className="px-5 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingUser ? 'Salvar Alterações' : 'Criar Usuário'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
